import React, { useState, useEffect, useRef } from "react";
import { Loading } from "seed/helpers";
import lodash from "lodash";
import { assignIdxNodes } from "components/studio/Controller.lib.index"
import * as parser from "components/studio/Controller.lib.parser";
import * as graph from "components/util/schema_form/util/graph";
import * as lib from "components/util/schema_form/fields/variable/VariableField.lib";
import View from "components/util/schema_form/fields/variable/VariableField.view";

function VariableField({
  nodeId,
  parentId,
  name,
  label,
  required,
  disabled,
  caption,
  helpMessage,
  dataReqs,
  acceptTypes = "all",
  openText = false,
  supportIterables = false,
  enableAttributes = true,
  placeholder = "Selecciona una referencia del proceso%SC146",
  textType = "text",
  selectorFilters = [],
  showNodeAddButton = true,
  showCollectionAddButton = false,
  showCollectionCondition = true,
  saveCollectionColumns,
  showConstants = false,
  displayAttributesManually = false,
  value,
  flowId,
  values,
  setFieldValue = () => { },
  onSaveCollectionSuggestion = () => { },
  onChange = () => { },
}) {

  const toolboxRef = useRef(null);
  const pageId = sessionStorage.getItem("page_" + flowId)
  const [showOptionsWhenEmpty, setShowOptionsWhenEmpty] = useState(!openText);
  const [showModal, setShowModal] = useState(false);
  const [showToolboxModal, setShowToolboxModal] = useState(false);
  const [pendingCollection, setPendingCollection] = useState(null);
  const [conditionFilterName] = useState(lib.getFilterName(name));

  if (openText && placeholder == "Selecciona una referencia del proceso%SC146")
    placeholder = "Ingresa un valor%SC147"

  useEffect(() => {
    if (value == null || (typeof value === 'object' && Object.keys(value).length === 0))
      setFieldValue(name, "")
  }, [flowId, nodeId]);

  const onClickArrow = () => setShowOptionsWhenEmpty(true);
  const onClickAddCollection = () => setShowModal(true)
  const onClickAddNode = () => setShowToolboxModal(true)
  const onCloseCollectionModal = () => setShowModal(false)
  const onCloseNodeModal = () => setShowToolboxModal(false)
  const onSaveCollection = (data) => {
    onSaveCollectionSuggestion("@" + data.name)
    setPendingCollection("@" + data.name)
    setShowModal(false);
    dataReqs.collections.refetch();
  }

  const onInputChange = (idx, inputValue, reason) => {
    let newState = lib.parseValueToState(
      value, nodes, collections, datatypes, selectorFilters, acceptTypes,
      showCollectionAddButton, showNodeAddButton, showConstants, supportIterables)
    newState = newState.slice(0, idx + 1)
    if (idx > 0 && reason == "clear") {
      let prevOption = newState[idx - 1].options.find(opt => opt.value == newState[idx - 1].selectedOption)
      if (acceptTypes == "all" || acceptTypes.includes(prevOption?.data?.type))
        newState = newState.slice(0, idx)
      else
        newState[idx].selectedOption = inputValue;
    } else
      newState[idx].selectedOption = inputValue;

    const newValue = lib.parseStateToValue(newState)
    const newType = newState[parseInt(idx)]?.options.find(opt => opt.value == inputValue)?.type
    const nextState = lib.parseValueToState(
      newValue + ".", nodes, collections, datatypes, selectorFilters, acceptTypes,
      showCollectionAddButton, showNodeAddButton, showConstants, supportIterables);
    const nextOptions = nextState[idx + 1]?.options ?? []

    const fieldValue = (reason == "select-option" || (acceptTypes != "all" && !acceptTypes.includes(newType))) &&
      nextOptions.length > 0 && enableAttributes && !displayAttributesManually ? (newValue + ".") : newValue
    setFieldValue(name, fieldValue);
    onChange(fieldValue)
  }

  const onCollectionFilterChange = (value, enabled = true) => {
    let newState = [...state]
    newState[0].condition = { enabled: enabled, ...value }
    const fieldValue = lib.parseStateToValue(state)
    setFieldValue(name, fieldValue)
    onChange(fieldValue)
  }

  const onConditionSelectChange = (value) => {
    if (value == "all") onCollectionFilterChange(null, false)
    else onCollectionFilterChange({ condition: "=" })
  }

  const onIterabilityChange = (value) => {
    if (value != null) {
      let newState = [...state]
      newState[0].iterability = lib.parseIterabilityStrToState(":" + value)
      const fieldValue = lib.parseStateToValue(state)
      setFieldValue(name, fieldValue)
      onChange(fieldValue)
    }
  }

  const onClickAttributeButton = () =>
    setFieldValue(name, value + ".")

  const onCompletedNodeCreated = () => { }

  if (dataReqs.nodes.loading || dataReqs.edges.loading || dataReqs.collections.loading || dataReqs.dataTypes.loading)
    return <Loading size={27} />;
  if (dataReqs.nodes.error || dataReqs.edges.error || dataReqs.collections.error || dataReqs.dataTypes.error)
    return "";

  let nodes = [];
  const tempNodes = [...dataReqs.nodes.data.flowNodes];
  const tempEdges = [...dataReqs.edges.data.flowEdges];
  const collections = dataReqs.collections.data.collections;
  const datatypes = dataReqs.dataTypes.data.appDatatypes

  // Assign IDX to nodes
  const parsedNodes = parser.parseNodesGraphqlToUi(tempNodes);
  const parsedEdges = parser.parseEdgesGraphqlToUi(tempEdges);
  const nodesWithIdx = assignIdxNodes(parsedNodes, parsedEdges);

  // Filter only nodes that are ancestors of the current node or the current parent
  const filteredNodes = graph.getAncerstorNodes(nodesWithIdx, parsedEdges, nodeId, parentId)

  for (const node of filteredNodes) {
    let currentNode = { ...tempNodes.find(nd => nd.id == node.data.remoteId) };
    if (currentNode && currentNode.action.outputStructure.length > 0 && currentNode.id != nodeId) {
      currentNode.idx = node.data.idx
      nodes.push(currentNode)
    }
  }
  const state = lib.parseValueToState(
    value, nodes, collections, datatypes, selectorFilters, acceptTypes,
    showCollectionAddButton, showNodeAddButton, showConstants, supportIterables);
  const nextState = lib.parseValueToState(
    value + ".", nodes, collections, datatypes, selectorFilters, acceptTypes,
    showCollectionAddButton, showNodeAddButton, showConstants, supportIterables);

  if (pendingCollection && state.length > 0) {
    let collectionOption = state[0].options.find(option => option.value == pendingCollection)
    if (collectionOption) {
      setPendingCollection(null)
      onInputChange(0, pendingCollection, "select-option")
    }
  }

  const saveCollectionSource = saveCollectionColumns?.source
  const saveCollectionValue = saveCollectionColumns?.value
  let saveCollectionSchema = []
  if (saveCollectionSource == "RAW")
    saveCollectionSchema = saveCollectionValue


  const selectedCollection = collections.find(
    collection => state.length > 0 && collection.name == (state[0].selectedOption ?? "").replaceAll("@", ""))

  let conditionStructure = selectedCollection ?
    [...(selectedCollection.schema?.structure ?? [])]
      .sort((a, b) => (b.name ?? "").startsWith("_") ? -1 : 0) // Move internal keys at the end (_internal_id, _created_at) 
    : null;
  const conditionColumn = (conditionStructure ?? []).find(
    column => state.length > 1 && column.name == (state[state.length - 1].selectedOption ?? "").replaceAll("@", "")
  ) ?? (conditionStructure ?? []).length > 0 ? conditionStructure[0].name : ""

  const conditionState = state.length > 0 ? state[0].condition : null
  const iterabilityState = state.length > 0 ? state[0].iterability : null

  return (
    <View
      name={name}
      label={label}
      nodeId={nodeId}
      nodes={nodes}
      required={required}
      disabled={disabled}
      openText={openText}
      enableAttributes={enableAttributes}
      caption={caption}
      helpMessage={helpMessage}
      placeholder={placeholder}
      textType={textType}
      flowId={flowId}
      dataReqs={dataReqs}
      values={values}
      pageId={pageId}
      toolboxRef={toolboxRef}
      state={state}
      nextState={nextState}
      showModal={showModal}
      showToolboxModal={showToolboxModal}
      showOptionsWhenEmpty={showOptionsWhenEmpty}
      conditionStructure={conditionStructure}
      conditionColumn={conditionColumn}
      conditionState={conditionState}
      conditionFilterName={conditionFilterName}
      iterabilityState={iterabilityState}
      saveCollectionSchema={saveCollectionSchema}
      showCollectionCondition={showCollectionCondition}
      setFieldValue={setFieldValue}
      onInputChange={onInputChange}
      onClickArrow={onClickArrow}
      onClickAttributeButton={onClickAttributeButton}
      onCloseNodeModal={onCloseNodeModal}
      onCloseCollectionModal={onCloseCollectionModal}
      onSaveCollection={onSaveCollection}
      onClickAddCollection={onClickAddCollection}
      onClickAddNode={onClickAddNode}
      onCompletedNodeCreated={onCompletedNodeCreated}
      onConditionSelectChange={onConditionSelectChange}
      onCollectionFilterChange={onCollectionFilterChange}
      onIterabilityChange={onIterabilityChange}
    />
  );
}

export default VariableField;